import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, BookOpen, Eye, FolderInput, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorText } from "@/lib/api";
import { pick } from "@/lib/format";
import { imgSrc } from "@/lib/media";
import { useAdminLang } from "./context";
import { SortableList, SortableRow, moveId } from "./Sortable";
import { AssignDialog, MenuEditor } from "./MenuEditor";

const bodyOf = ({ id, sort_order, created_at, updated_at, category_count, ...b }) => b;

const MenuRow = ({ m, handle, idx, total, t, lang, onToggle, onMove, onEdit, onAssign, onDelete }) => (
  <div className={`flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3 sm:flex-nowrap ${m.is_visible ? "" : "opacity-60"}`} data-testid={`admin-menu-row-${m.id}`}>
    <button type="button" {...handle} aria-label="Drag" className="cursor-grab touch-none text-muted-foreground" data-testid={`menu-drag-${m.id}`}><GripVertical className="h-5 w-5" /></button>
    <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-secondary">
      {m.image_url ? <img src={imgSrc(m.image_url, "sm")} alt="" className="h-full w-full object-cover" /> : <BookOpen className="absolute inset-0 m-auto h-5 w-5 text-muted-foreground" />}
      {m.logo_url && <img src={imgSrc(m.logo_url, "sm")} alt="" className="absolute bottom-1 start-1 h-6 w-6 rounded bg-black/50 object-contain p-0.5" />}
    </div>
    <div className="min-w-0 flex-1 basis-40 space-y-0.5">
      <button type="button" onClick={() => onEdit(m)} className="truncate font-semibold hover:text-primary" data-testid={`menu-name-${m.id}`}>{pick(m, "name", lang).text}</button>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground"><bdi dir="ltr">/menu/{m.slug}</bdi><span>{t("mn.categories", { n: m.category_count })}</span></div>
    </div>
    <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"><Switch checked={m.is_visible} onCheckedChange={(v) => onToggle(m, v)} data-testid={`menu-visible-toggle-${m.id}`} />{t("items.visible")}</label>
    <div className="flex shrink-0 items-center">
      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0} onClick={() => onMove(m, -1)} aria-label={t("items.moveUp")} data-testid={`menu-move-up-${m.id}`}><ArrowUp className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === total - 1} onClick={() => onMove(m, 1)} aria-label={t("items.moveDown")} data-testid={`menu-move-down-${m.id}`}><ArrowDown className="h-4 w-4" /></Button>
      <Button variant="ghost" size="sm" onClick={() => onAssign(m)} data-testid={`menu-assign-${m.id}`}><FolderInput className="h-4 w-4" /><span className="hidden md:inline">{t("mn.assign")}</span></Button>
      <Button asChild variant="ghost" size="icon" className="h-8 w-8"><a href={`/preview/menu/${m.slug}`} target="_blank" rel="noreferrer" aria-label={t("mn.preview")} data-testid={`menu-preview-${m.id}`}><Eye className="h-4 w-4" /></a></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(m)} aria-label={t("items.edit")} data-testid={`menu-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => onDelete(m)} aria-label={t("items.delete")} data-testid={`menu-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  </div>
);

export default function Menus() {
  const { t, lang } = useAdminLang();
  const { refresh } = useOutletContext();
  const [menus, setMenus] = useState([]);
  const [cats, setCats] = useState([]);
  const [editor, setEditor] = useState(null);
  const [assign, setAssign] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => Promise.all([api.get("/admin/menus"), api.get("/admin/categories")])
    .then(([a, b]) => { setMenus(a.data); setCats(b.data); })
    .catch(() => toast.error(t("toast.loadFailed"))), [t]);
  useEffect(() => {
    load();
  }, [load]);
  const changed = () => { load(); refresh(); };

  const reorder = async (ids) => {
    setMenus((prev) => ids.map((id) => prev.find((m) => m.id === id)));
    try {
      await api.put("/admin/menus/reorder", { ids });
      toast.success(t("toast.orderSaved"));
      refresh();
    } catch {
      toast.error(t("toast.saveFailed"));
      load();
    }
  };
  const onToggle = async (m, v) => {
    setMenus((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_visible: v } : x)));
    try {
      await api.put(`/admin/menus/${m.id}`, bodyOf({ ...m, is_visible: v }));
      refresh();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
      load();
    }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/menus/${deleting.id}`);
      toast.success(t("toast.deleted"));
      changed();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    }
    setDeleting(null);
  };
  const ids = menus.map((m) => m.id);

  return (
    <div className="space-y-6" data-testid="admin-menus-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{t("mn.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("mn.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="secondary" className="rounded-full"><a href="/preview" target="_blank" rel="noreferrer" data-testid="menus-preview-landing"><Eye className="h-4 w-4" />{t("mn.previewLanding")}</a></Button>
          <Button onClick={() => setEditor({ key: Date.now(), menu: null })} className="rounded-full" data-testid="add-menu-btn"><Plus className="h-4 w-4" />{t("mn.add")}</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t("mn.publishHint")}</p>
      {!menus.length && <p className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground" data-testid="menus-empty">{t("mn.empty")}</p>}
      <SortableList ids={ids} onReorder={reorder}>
        <div className="space-y-2">
          {menus.map((m, idx) => (
            <SortableRow key={m.id} id={m.id}>
              {(handle) => <MenuRow m={m} handle={handle} idx={idx} total={menus.length} t={t} lang={lang} onToggle={onToggle} onMove={(x, d) => { const n = moveId(ids, x.id, d); if (n) reorder(n); }}
                onEdit={(x) => setEditor({ key: Date.now(), menu: x })} onAssign={setAssign} onDelete={setDeleting} />}
            </SortableRow>
          ))}
        </div>
      </SortableList>
      {editor && <MenuEditor key={editor.key} menu={editor.menu} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); changed(); }} />}
      {assign && <AssignDialog menu={assign} categories={cats} onClose={() => setAssign(null)} onSaved={() => { setAssign(null); changed(); }} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-menu-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("mn.deleteTitle", { name: deleting ? pick(deleting, "name", lang).text : "" })}</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.category_count ? t("mn.deleteBlocked", { n: deleting.category_count }) : t("mn.deleteBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            {!deleting?.category_count && <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="delete-menu-confirm">{t("common.delete")}</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
